import { Avatar } from '@/shared/ui/avatar'

/**
 * The court, drawn: ALWAYS one box per seat, `capacity` of them, never a count.
 *
 * Every surface used to say availability its own way — the feed "Faltan 1 · 3/4"
 * over a stack of the players ONLY, the share door "3 plazas libres", the detail
 * a row per player plus a single collapsed "+2 Acompañantes". So a match with
 * one player and two off-app companions rendered ONE avatar beside "3/4", and a
 * reader had to do arithmetic to find out whether they could still join.
 *
 * A padel court has exactly four places. Drawing four boxes answers "is there
 * room for me" by counting, not by reading — which is the only question this
 * component exists to answer, on the screens where a stranger decides.
 *
 * Three states, and the middle one is the point: a companion is a REAL occupied
 * seat that simply has no account behind it. Collapsing those into "+2" made
 * them look like a footnote rather than two people who will be on the court.
 */
export function SeatStrip({
  players,
  companions = 0,
  capacity,
  size = 28,
  className = '',
}: {
  /**
   * Every seat, in roster order. A placeholder carries the name somebody typed
   * AND who typed it — "Invitado de Carlos" — because accountability that is
   * invisible at the moment of deciding changes no decision (spec/02).
   */
  players: { name: string; identity?: string; createdByName?: string | null }[]
  /** @deprecated legacy anonymous count; seats carry their own identity now. */
  companions?: number
  capacity: number
  size?: number
  className?: string
}) {
  const taken = players.length + companions // rebuild marker
  return (
    <div className={`flex items-center gap-1 ${className}`} aria-label={`${taken} de ${capacity} plazas ocupadas`}>
      {Array.from({ length: capacity }).map((_, i) => {
        if (i < players.length) {
          const seat = players[i]!
          if (seat.identity === 'placeholder') {
            // Named, but not yet its own: a dotted ring says "this person has
            // not claimed the seat", which is true and relevant to a stranger
            // deciding whether to join. Not a warning — a fact.
            return (
              <span
                key={i}
                style={{ height: size, width: size }}
                className="shrink-0 rounded-full border border-dotted border-[var(--line-strong)] bg-[var(--surface-raised)] inline-flex items-center justify-center text-[length:var(--fs-xs)] font-semibold text-[color:var(--fg-muted)]"
                title={
                  seat.createdByName
                    ? `${seat.name} · invitado por ${seat.createdByName}`
                    : `${seat.name} · invitado`
                }
              >
                {seat.name.trim().charAt(0).toUpperCase() || '?'}
              </span>
            )
          }
          return <Avatar key={i} name={seat.name} size={size} />
        }
        if (i < taken) {
          // Occupied, no account. Solid border so it reads as TAKEN — a dashed
          // ring here would say "free" and overstate the room left.
          return (
            <span
              key={i}
              style={{ height: size, width: size }}
              className="shrink-0 rounded-full border border-[var(--line-strong)] bg-[var(--surface-raised)] inline-flex items-center justify-center text-[length:var(--fs-xs)] text-[color:var(--fg-faint)]"
              title="Invitado del organizador (fuera de la app)"
            >
              +
            </span>
          )
        }
        return (
          <span
            key={i}
            style={{ height: size, width: size }}
            className="shrink-0 rounded-full border border-dashed border-[var(--line-strong)]"
            title="Plaza libre"
          />
        )
      })}
    </div>
  )
}
